import React, { memo, useCallback, useMemo, useState } from 'react'
import { Controller, FieldErrors, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import get from 'lodash/get'
import {
  ControlledCheckbox,
  ControlledCurrencyInput,
  ControlledSelect,
  ControlledTextField,
} from 'transfix-form/components'
import ControlledDropzone from 'transfix-form/components/ControlledDropzone'
import { Button } from 'transfix-ui/components/Button'
import ButtonGroup from 'transfix-ui/components/ButtonGroup'
import Card from 'transfix-ui/components/Card'
import { Grid, Link, makeStyles, Typography } from 'transfix-ui/core'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ICarrierResultFragment } from 'apps/marketplace/components/CarrierAutosuggest/graphql/CarrierResultFragment'
import ControlledCarrierAutosuggest from 'apps/marketplace/components/ControlledCarrierAutosuggest'
import { AttachmentIcon, DeleteIcon } from 'transfix-icons'
import { ConfirmationModal, IconButton, IModalAction } from 'transfix-ui'
import { useShipperRateContext } from '../../../context/ShipperRateContext'
import { LaneHeader } from '../../LaneHeader'
import { rateTypeKeySelectOptions } from '../constants'
import { ISourcedCarrierRateInfoFragment } from '../graphql/SourcedCarrierRateInfoFragment'
import {
  CapacityType,
  TEditSourcedCarrierRateModalSchema,
} from './EditSourcedCarrierRateModalSchema'
import { useEditSourcedCarrierRateModalFormContext } from './useEditSourcedCarrierRateModalForm'
import { useLaneAgreementPreview } from './useLaneAgreementPreview'

const useSourcedCarrierRateFormStyles = makeStyles({
  container: {
    padding: 16,
    margin: 0,
    maxWidth: '100%',
  },
  laneAgreementLink: {
    marginLeft: '10px',
    marginRight: 'auto',
  },
  hidden: {
    display: 'none',
  },
})

const getErrorMessage = <T extends FieldErrors<TEditSourcedCarrierRateModalSchema>>(
  errors: T,
  keyName: string
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

const labelGenerator = (carrier: Partial<ICarrierResultFragment>): string => {
  const compliment =
    carrier.mcNumber && carrier.dotNumber
      ? ` | ID: ${carrier.id} | MC: ${carrier.mcNumber} | DOT: ${carrier.dotNumber}`
      : ''
  return `${carrier.name}${compliment}`
}

interface IEditSourcedCarrierRateModalFormProps {
  defaultCarrier?: ISourcedCarrierRateInfoFragment['carrierApplication']
  hasExistingLaneAgreement: boolean
}

export const EditSourcedCarrierRateModalForm = memo<IEditSourcedCarrierRateModalFormProps>(
  ({ defaultCarrier, hasExistingLaneAgreement }) => {
    const { control, errors, setValue } = useEditSourcedCarrierRateModalFormContext()
    const [resetDropzoneModalOpen, setResetDropzoneModalOpen] = useState<boolean>(false)
    const { getFlag } = useFeatureFlagContext()
    const { enabled: laneAgreementsEnabled } = getFlag('laneAgreements')

    const closeResetDropzoneModal = useCallback(() => setResetDropzoneModalOpen(false), [])
    const openResetDropzoneModal = useCallback(() => setResetDropzoneModalOpen(true), [])

    const classes = useSourcedCarrierRateFormStyles()
    const { selectedShipperRate } = useShipperRateContext()

    const capacityType = useWatch<TEditSourcedCarrierRateModalSchema['capacityType']>({
      control,
      name: 'capacityType',
      defaultValue: CapacityType.single,
    })

    const isRange = capacityType === CapacityType.range

    const [laneAgreement] = useWatch<TEditSourcedCarrierRateModalSchema['laneAgreements']>({
      control,
      name: 'laneAgreements',
      defaultValue: [],
    })

    const { openLaneAgreementPreview } = useLaneAgreementPreview()

    const resetDropzone = useCallback(() => {
      setValue('laneAgreements', [])
      setResetDropzoneModalOpen(false)
    }, [setValue])

    const minCapacityContainerClassName = classNames({
      [classes.hidden]: capacityType === CapacityType.single,
    })

    const resetDropzoneModalActions: IModalAction[] = useMemo(
      () => [
        {
          type: 'dismiss',
          label: 'Cancel',
          action: closeResetDropzoneModal,
        },
        {
          action: () => resetDropzone(),
          label: 'Delete',
          type: 'delete',
        },
      ],
      [closeResetDropzoneModal, resetDropzone]
    )

    return (
      <Grid className={classes.container} container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='h3'>
            <LaneHeader rate={selectedShipperRate} />
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card data-test='sourced-carrier-rate-form' title='Step 1: Add Rate'>
            <Grid container spacing={2}>
              <Grid container item xs={12}>
                <Typography color='textSecondary'>Input data and add rate.</Typography>
              </Grid>

              <Grid alignItems='flex-start' container item spacing={2} xs={12}>
                <Grid item xs={4}>
                  <ControlledCarrierAutosuggest
                    name='carrierApplicationId'
                    control={control}
                    carrier={defaultCarrier}
                    defaultValue={defaultCarrier?.id ?? ''}
                    optionLabelGenerator={labelGenerator}
                    AutocompleteProps={{
                      label: 'Carrier',
                      required: true,
                      size: 'small',
                      placeholder: 'Enter Carrier Name, ID, MC or DOT',
                      inputError: Boolean(getErrorMessage(errors, 'carrierApplicationId')),
                    }}
                  />
                </Grid>

                <Grid item>
                  <ControlledCurrencyInput
                    name='rate.amount'
                    control={control}
                    CurrencyInputProps={{
                      dataTest: 'rate-amount',
                      invalid: Boolean(getErrorMessage(errors, 'rate.amount')),
                      label: 'Rate Amount',
                      placeholder: 'Enter Amount',
                      validationMessage: getErrorMessage(errors, 'rate.amount'),
                    }}
                  />
                </Grid>

                <Grid item xs={2}>
                  <ControlledSelect
                    name='rate.typeKey'
                    control={control}
                    SelectProps={{
                      dataTest: 'rate-type',
                      error: Boolean(getErrorMessage(errors, 'rate.type')),
                      label: 'Rate Type',
                      options: rateTypeKeySelectOptions,
                      placeholder: 'Select One',
                      required: true,
                      validationMessage: getErrorMessage(errors, 'rate.type'),
                    }}
                  />
                </Grid>

                <Grid item>
                  <Controller
                    name='capacityType'
                    control={control}
                    render={({ onChange, value }) => {
                      const handleChange = (capacityType: string) => {
                        onChange(capacityType)
                      }

                      return (
                        <ButtonGroup
                          label='Capacity Type'
                          value={value}
                          onChange={handleChange}
                          dataTest='capacity-type'>
                          <Button value={CapacityType.single} data-test='single-toggle'>
                            Single
                          </Button>
                          <Button value={CapacityType.range} data-test='range-toggle'>
                            Range
                          </Button>
                        </ButtonGroup>
                      )
                    }}
                  />
                </Grid>

                <Grid className={minCapacityContainerClassName} item>
                  <ControlledTextField
                    name='minCapacity'
                    control={control}
                    defaultValue=''
                    TextInputProps={{
                      clearable: false,
                      dataTest: 'min-capacity',
                      error: Boolean(getErrorMessage(errors, 'minCapacity')),
                      label: 'Min Weekly Capacity',
                      type: 'number',
                      validationMessage: getErrorMessage(errors, 'minCapacity'),
                    }}
                  />
                </Grid>

                <Grid item>
                  <ControlledTextField
                    name='maxCapacity'
                    control={control}
                    defaultValue=''
                    TextInputProps={{
                      clearable: false,
                      dataTest: 'max-capacity',
                      error: Boolean(getErrorMessage(errors, 'maxCapacity')),
                      label: `${isRange ? 'Max ' : ''}Weekly Capacity`,
                      type: 'number',
                      validationMessage: getErrorMessage(errors, 'maxCapacity'),
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container direction='column' item xs={12}>
                <Grid item>
                  <ControlledCheckbox
                    control={control}
                    name='canDoWeekends'
                    CheckboxProps={{
                      inputProps: {
                        'data-test': 'can-do-weekends',
                      },
                      label: 'Can do Weekends',
                    }}
                  />
                </Grid>

                <Grid item>
                  <ControlledCheckbox
                    control={control}
                    name='confirmed'
                    CheckboxProps={{
                      inputProps: {
                        'data-test': 'confirmed',
                      },
                      label: 'Confirmed',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {laneAgreementsEnabled && (
          <Grid item xs={12}>
            <Card title='Step 2: Upload Signed Lane Agreement'>
              <Grid container spacing={2}>
                <Grid container item xs={12}>
                  <Typography color='textSecondary'>
                    Upload the signed Lane Agreement.{' '}
                    {hasExistingLaneAgreement &&
                      'This will replace the existing Lane Agreement for this Carrier.'}
                  </Typography>
                </Grid>

                <Grid
                  className={classNames({
                    [classes.hidden]: !laneAgreement,
                  })}
                  item
                  xs={12}>
                  <Card>
                    <Grid alignItems='center' container spacing={2}>
                      <AttachmentIcon size='large' />

                      <Link
                        className={classes.laneAgreementLink}
                        onClick={() => openLaneAgreementPreview(laneAgreement)}>
                        {laneAgreement?.fileFileName}
                      </Link>

                      <IconButton Icon={DeleteIcon} size='large' onClick={openResetDropzoneModal} />
                    </Grid>

                    <ConfirmationModal
                      actions={resetDropzoneModalActions}
                      open={resetDropzoneModalOpen}
                      onClose={closeResetDropzoneModal}>
                      Are you sure that you want to delete this attachment?
                    </ConfirmationModal>
                  </Card>
                </Grid>

                <Grid
                  className={classNames({
                    [classes.hidden]: Boolean(laneAgreement),
                  })}
                  item
                  xs={12}>
                  <ControlledDropzone
                    name='laneAgreements'
                    control={control}
                    defaultValue={[]}
                    DropzoneProps={{
                      acceptedFiles: ['application/pdf', 'application/x-pdf'],
                      dropZoneText: 'Drop your Lane Agreement here',
                      filesLimit: 1,
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
      </Grid>
    )
  }
)
